/**
 * Overview — landing page that proves the design system end-to-end.
 *
 * All data is currently STATIC FIXTURES so the UI is fully clickable
 * before the backend wiring lands. Once the Python API is connected,
 * replace the const fixtures with calls into `@/api/client` + Zustand.
 */
import { useEffect, useMemo } from "react";
import { ArrowRight, RefreshCw, ShieldAlert, Activity, Server, Layers } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { KpiCard } from "@/components/ui/kpi-card";
import { Card, CardHeader, CardTitle, CardSub, CardContent } from "@/components/ui/card";
import {
  TableShell,
  TableHeadRow,
  TableRow,
  TableHeadCell,
  TableCell,
} from "@/components/ui/table-shell";
import { useCompliance } from "@/stores/compliance";
import { useWorkflowRuns } from "@/stores/workflow-runs";
import { useAuditLog } from "@/stores/audit";
import { useInventory } from "@/stores/inventory";
import { useSyncStatus } from "@/stores/sync-status";
import { WelcomeModal } from "@/components/app/welcome-modal";


export function OverviewPage() {
  // Real compliance KPI — drift across most-recent scans per
  // (network, preset). Replaces the legacy fake "Errors 24h" tile.
  const startPolling = useSyncStatus((s) => s.startPolling);
  const history     = useCompliance((s) => s.history);
  const loadHistory = useCompliance((s) => s.loadHistory);
  // Real workflow KPIs — feeds the Active-workflows + Configs-queued tiles.
  const runs     = useWorkflowRuns((s) => s.data);
  const loadRuns = useWorkflowRuns((s) => s.load);
  useEffect(() => { void loadHistory(); }, [loadHistory]);
  useEffect(() => { void loadRuns(); }, [loadRuns]);
  const workflowKpi = useMemo(() => {
    let running = 0;
    let queued = 0;
    for (const r of runs) {
      if (r.state === "running") running += 1;
      else if (r.state === "queued") queued += 1;
    }
    return { running, queued };
  }, [runs]);
  // Real recent-activity feed — the last few audit events. The audit
  // store already paginates server-side with newest-first ordering, so
  // we just slice the first N for the preview card.
  const auditEvents = useAuditLog((s) => s.data);
  const loadAudit   = useAuditLog((s) => s.load);
  useEffect(() => { void loadAudit(); }, [loadAudit]);
  const recentEvents = useMemo(() => auditEvents.slice(0, 7), [auditEvents]);
  // Real 24h push stats — derived from the same workflow-runs store the
  // KPI tiles use. Avoids a second roundtrip. Buckets by hour for the
  // bar chart at the bottom of the card.
  const pushStats = useMemo(() => {
    // eslint-disable-next-line react-hooks/purity
    const now = Date.now();
    const cutoff = now - 24 * 3600_000;
    const finishedStates = new Set<string>(["success", "failed", "cancelled"]);
    type Bucket = { hourStart: number; success: number; failed: number };
    const buckets: Bucket[] = [];
    for (let i = 23; i >= 0; i -= 1) {
      buckets.push({ hourStart: now - i * 3600_000, success: 0, failed: 0 });
    }
    let total = 0;
    let success = 0;
    const durationsMs: number[] = [];
    for (const r of runs) {
      const triggered = new Date(r.triggeredAt).getTime();
      if (Number.isNaN(triggered) || triggered < cutoff) continue;
      if (!finishedStates.has(r.state)) continue;
      total += 1;
      if (r.state === "success") success += 1;
      if (typeof r.durationMs === "number" && r.durationMs >= 0) {
        durationsMs.push(r.durationMs);
      }
      // Bucket index — clamp into [0, 23]. Each bucket spans 1 hour.
      const idx = Math.min(23, Math.max(0, Math.floor((triggered - (now - 24 * 3600_000)) / 3600_000)));
      if (r.state === "success") buckets[idx].success += 1;
      else buckets[idx].failed  += 1;
    }
    durationsMs.sort((a, b) => a - b);
    const avgMs = durationsMs.length === 0 ? 0
      : Math.round(durationsMs.reduce((a, b) => a + b, 0) / durationsMs.length);
    const p95Ms = durationsMs.length === 0 ? 0
      : durationsMs[Math.min(durationsMs.length - 1, Math.floor(durationsMs.length * 0.95))];
    return {
      total,
      success,
      failed: total - success,
      successPct: total === 0 ? 100 : (success / total) * 100,
      avgMs,
      p95Ms,
      buckets,
    };
  }, [runs]);
  // Cross-vendor inventory feeds the Devices online KPI tile. Uses the
  // same /devices/ endpoint as the Devices page — cached at the store
  // level, so loading it here doesn't double-fetch when the user clicks
  // through.
  const inventory     = useInventory((s) => s.data);
  const loadInventory = useInventory((s) => s.load);
  useEffect(() => { void loadInventory(); }, [loadInventory]);
  const deviceKpi = useMemo(() => {
    let online = 0;
    for (const d of inventory) {
      if (d.status === "online") online += 1;
    }
    return { online, total: inventory.length };
  }, [inventory]);
  // Sites by device count — replaces the old "Site health · top regions"
  // hardcoded list. Groups the cross-vendor inventory by site, counts
  // online vs total, and shows the top 5 by total. A real, useful "where
  // is my fleet concentrated" rollup with no fake data.
  const topSites = useMemo(() => {
    type Row = { site: string; online: number; total: number };
    const byName = new Map<string, Row>();
    for (const d of inventory) {
      const key = d.site || "Unassigned";
      const row = byName.get(key) ?? { site: key, online: 0, total: 0 };
      row.total += 1;
      if (d.status === "online") row.online += 1;
      byName.set(key, row);
    }
    return [...byName.values()].sort((a, b) => b.total - a.total).slice(0, 5);
  }, [inventory]);
  const driftKpi = useMemo(() => {
    // Reduce to one row per (networkId, presetSlug), keeping the newest.
    const latest: Record<string, typeof history[number]> = {};
    for (const h of history) {
      const k = `${h.networkId}::${h.presetSlug}`;
      const cur = latest[k];
      if (!cur || h.ranAt > cur.ranAt) latest[k] = h;
    }
    const rows = Object.values(latest);
    const drifted = rows.filter((r) => (r.summary["non-compliant"] ?? 0) > 0).length;
    const total   = rows.length;
    const trend: "up" | "down" | "flat" =
      total === 0 ? "flat"
      : drifted === 0 ? "down"
      : drifted >= Math.ceil(total / 2) ? "up"
      : "flat";
    return { drifted, total, trend };
  }, [history]);

  return (
    <div className="flex flex-col gap-6">
      {/* First-login orientation. Self-gates on auth.user.welcomedAt — no-op
          for returning users. */}
      <WelcomeModal />
      {/* Crumb */}
      <div className="flex items-center gap-2 text-xs text-fg-mute">
        <span className="text-fg-dim">Operations</span>
        <span className="opacity-40">/</span>
        <span className="text-fg-dim">Production</span>
        <span className="opacity-40">/</span>
        <span className="text-fg">Overview</span>
      </div>

      {/* Hero */}
      <div className="flex items-end justify-between gap-6">
        <div>
          <h1 className="m-0 text-3xl font-extrabold tracking-tight text-aurora">Operations Overview</h1>
          <p className="mt-1.5 max-w-[620px] text-sm text-fg-dim">
            Live operational view across your configured vendors. Drill into a
            vendor page for tenant-specific state, or open Compliance for the
            latest drift snapshot.
          </p>
        </div>
        <div className="flex gap-2.5">
          <Button variant="ghost" size="md" onClick={startPolling}>
            <RefreshCw size={14} />
            Sync now
          </Button>
          <Button variant="primary" size="md">
            <ArrowRight size={14} />
            Push config
          </Button>
        </div>
      </div>


      {/* KPIs */}
      <div className="grid grid-cols-4 gap-3.5">
        <Link to="/devices" className="block no-underline" title="Open Devices inventory">
          <KpiCard
            label="Devices online"
            value={deviceKpi.total === 0 ? "—" : `${deviceKpi.online}/${deviceKpi.total}`}
            trend={
              deviceKpi.total === 0
                ? "flat"
                : deviceKpi.online === deviceKpi.total ? "up" : "down"
            }
            trendLabel={
              deviceKpi.total === 0
                ? "configure a vendor key"
                : deviceKpi.online === deviceKpi.total
                  ? "all reachable"
                  : `${deviceKpi.total - deviceKpi.online} not online`
            }
            icon={<Server size={14} />}
          />
        </Link>
        <Link to="/workflows" className="block no-underline" title="Open workflows page">
          <KpiCard
            label="Active workflows"
            value={String(workflowKpi.running)}
            trend={workflowKpi.running > 0 ? "up" : "flat"}
            trendLabel={workflowKpi.running === 0 ? "no runs in flight" : "currently running"}
            icon={<Activity size={14} />}
          />
        </Link>
        <Link to="/workflows" className="block no-underline" title="Open workflows page">
          <KpiCard
            label="Configs queued"
            value={String(workflowKpi.queued)}
            trend={workflowKpi.queued > 0 ? "up" : "flat"}
            trendLabel={workflowKpi.queued === 0 ? "queue empty" : "waiting to run"}
            icon={<Layers size={14} />}
          />
        </Link>
        <Link to="/compliance" className="block no-underline" title="Open compliance dashboard">
          <KpiCard
            label="Compliance drift"
            value={driftKpi.total === 0 ? "—" : `${driftKpi.drifted}/${driftKpi.total}`}
            trend={driftKpi.trend}
            trendLabel={driftKpi.total === 0
              ? "no scans yet"
              : `${driftKpi.drifted} of ${driftKpi.total} (network, preset) drifting`}
            icon={<ShieldAlert size={14} />}
          />
        </Link>
      </div>

      <div className="grid grid-cols-[1.2fr_1fr] gap-4">
        <WorkflowRunsCard pushStats={pushStats} />
        <TopSitesCard topSites={topSites} />
      </div>

      <RecentActivityTable recentEvents={recentEvents} />
    </div>
  );
}

/* ---------- overview sub-cards ---------- */

const ACTIVITY_COLS = "0.7fr 1fr 0.7fr 0.7fr 2.4fr";

type PushStats = {
  total: number; success: number; failed: number; successPct: number;
  avgMs: number; p95Ms: number;
  buckets: { hourStart: number; success: number; failed: number }[];
};

function WorkflowRunsCard({ pushStats }: { pushStats: PushStats }) {
  return (
    <Card>
      <CardHeader>
        <div>
          <CardTitle>Workflow runs — last 24 hours</CardTitle>
          <CardSub>
            All vendors · finished only · success vs failed. Derived
            from <code className="rounded bg-surface-2 px-1 py-0.5 font-mono text-[11px]">/workflows/runs/</code>.
          </CardSub>
        </div>
      </CardHeader>
      <CardContent>
        <div className="mb-4 flex gap-6 px-1">
          <div>
            <div className="text-[11px] text-fg-mute">Total runs</div>
            <div className="text-aurora text-lg font-bold">{pushStats.total}</div>
          </div>
          <div>
            <div className="text-[11px] text-fg-mute">Success rate</div>
            <div className="text-lg font-bold">{pushStats.total === 0 ? "—" : `${pushStats.successPct.toFixed(1)}%`}</div>
          </div>
          <div>
            <div className="text-[11px] text-fg-mute">Avg duration</div>
            <div className="text-lg font-bold">{pushStats.avgMs === 0 ? "—" : formatMs(pushStats.avgMs)}</div>
          </div>
          <div>
            <div className="text-[11px] text-fg-mute">P95</div>
            <div className="text-lg font-bold">{pushStats.p95Ms === 0 ? "—" : formatMs(pushStats.p95Ms)}</div>
          </div>
        </div>
        {pushStats.total === 0 ? (
          <div className="flex h-[120px] items-center justify-center rounded-lg border border-dashed border-line bg-surface-2/40 text-xs text-fg-mute">
            No runs in the last 24 hours.
          </div>
        ) : (
          <RunHistogram buckets={pushStats.buckets} />
        )}
      </CardContent>
    </Card>
  );
}

function TopSitesCard({ topSites }: { topSites: { site: string; online: number; total: number }[] }) {
  return (
    <Card>
      <CardContent>
        <CardTitle>Top sites by device count</CardTitle>
        <CardSub className="mb-3.5">
          Derived from your cross-vendor inventory. Bar fill = online / total. Click into{" "}
          <Link to="/sites" className="text-violet underline">Sites</Link>{" "}
          for the per-site dashboards.
        </CardSub>
        {topSites.length === 0 ? (
          <div className="rounded-lg border border-dashed border-line bg-surface-2/40 px-3 py-6 text-center text-xs text-fg-mute">
            No devices in inventory yet — add a vendor key and refresh.
          </div>
        ) : (
          <div className="flex flex-col gap-3 text-sm">
            {topSites.map((r) => {
              const pct = r.total === 0 ? 0 : Math.round((r.online / r.total) * 100);
              const grad =
                pct >= 95 ? "from-mint to-cyan"
                : pct >= 80 ? "from-violet to-cyan"
                : "from-peach to-rose";
              return (
                <div key={r.site}>
                  <div className="mb-1 flex justify-between">
                    <b className="truncate text-fg" title={r.site}>{r.site}</b>
                    <span className="font-mono text-fg-mute">{r.online} / {r.total}</span>
                  </div>
                  <div className="h-1.5 overflow-hidden rounded-full bg-line/40">
                    <div className={`h-full rounded-full bg-gradient-to-r ${grad}`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function RecentActivityTable({ recentEvents }: { recentEvents: { id: string; occurredAt: string; user?: string | null; kind: string; vendor?: string | null; summary: string }[] }) {
  return (
    <Card>
      <CardHeader>
        <div>
          <CardTitle>Recent activity</CardTitle>
          <CardSub>
            Last 7 audit events across all vendors —{" "}
            <Link to="/audit" className="text-violet underline">open full log</Link>
          </CardSub>
        </div>
      </CardHeader>
      <TableShell className="rounded-none border-0 border-t border-line">
        <TableHeadRow columns={ACTIVITY_COLS}>
          <TableHeadCell>When</TableHeadCell>
          <TableHeadCell>Who</TableHeadCell>
          <TableHeadCell>Kind</TableHeadCell>
          <TableHeadCell>Vendor</TableHeadCell>
          <TableHeadCell>Summary</TableHeadCell>
        </TableHeadRow>
        {recentEvents.map((e) => (
          <TableRow key={e.id} columns={ACTIVITY_COLS}>
            <TableCell className="font-mono text-[11px] text-fg-dim">{timeAgo(e.occurredAt)}</TableCell>
            <TableCell className="text-fg-dim">{e.user ?? "—"}</TableCell>
            <TableCell>
              <Badge variant={kindBadge(e.kind)} size="sm">{e.kind}</Badge>
            </TableCell>
            <TableCell className="font-mono text-[10.5px] uppercase tracking-wide text-fg-dim">
              {e.vendor ?? "system"}
            </TableCell>
            <TableCell className="text-fg" title={e.summary}>
              <span className="block max-w-[480px] truncate">{e.summary}</span>
            </TableCell>
          </TableRow>
        ))}
        {recentEvents.length === 0 ? (
          <div className="px-4 py-8 text-center text-xs text-fg-mute">
            No audit events yet — actions you take in Opus will appear here.
          </div>
        ) : null}
      </TableShell>
    </Card>
  );
}

/** Compact ms-to-human formatter for the 24h push stat strip. */
function formatMs(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.round(ms / 60_000)}m`;
}

/** Stacked bar histogram: 24 hourly buckets, success in mint, failed
 *  stacked on top in rose. Uses inline SVG so we don't pull a chart
 *  dep into the Overview bundle. */
function RunHistogram({ buckets }: { buckets: Array<{ hourStart: number; success: number; failed: number }> }) {
  const peak = Math.max(1, ...buckets.map((b) => b.success + b.failed));
  const W = 600;
  const H = 120;
  const padTop = 8;
  const padBot = 20;
  const barW = W / buckets.length;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="block h-[120px] w-full">
      <g stroke="rgb(var(--line))" strokeWidth="1" opacity="0.4">
        <line x1="0" y1={padTop} x2={W} y2={padTop} />
        <line x1="0" y1={H - padBot} x2={W} y2={H - padBot} />
      </g>
      {buckets.map((b, i) => {
        const total = b.success + b.failed;
        if (total === 0) return null;
        const usableH = H - padTop - padBot;
        const totalH = (total / peak) * usableH;
        const successH = (b.success / peak) * usableH;
        const x = i * barW + 1;
        const w = Math.max(1, barW - 2);
        return (
          <g key={b.hourStart}>
            <title>
              {new Date(b.hourStart).toLocaleString()}: {b.success} ok · {b.failed} failed
            </title>
            <rect x={x} y={H - padBot - totalH} width={w} height={totalH - successH} fill="rgb(var(--rose))" rx={1} />
            <rect x={x} y={H - padBot - successH} width={w} height={successH} fill="rgb(var(--mint))" rx={1} />
          </g>
        );
      })}
      {/* Hour ticks every 6h. */}
      {[0, 6, 12, 18, 24].map((h) => {
        const x = h * barW;
        return (
          <text key={h} x={x} y={H - 4} fill="rgb(var(--fg-mute))" fontSize={10} textAnchor={h === 0 ? "start" : h === 24 ? "end" : "middle"}>
            {h === 24 ? "now" : `-${24 - h}h`}
          </text>
        );
      })}
    </svg>
  );
}

/** Map an audit kind to a Badge variant for the kind pill. */
function kindBadge(kind: string): "ok" | "info" | "warn" | "err" | "neutral" {
  switch (kind) {
    case "create": return "ok";
    case "update": return "info";
    case "delete": return "err";
    case "login":  return "ok";
    case "logout": return "neutral";
    case "login_failed": return "warn";
    default: return "neutral";
  }
}

function timeAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return `${Math.max(0, Math.floor(ms / 1000))}s ago`;
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`;
  return `${Math.floor(ms / 86_400_000)}d ago`;
}
